/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { isActionIncluded } from 'src/app/common/fancy-firebase/util/is-action-included';
import { ticketAddActions, ticketRemoveActions } from '../actions/ticket-actions';
import { ICombinedEdit } from '../interfaces/combined-edit.interface';
import { GroupedTicket } from '@touchplan/ticket';
import { Ticket } from '@touchplan/common';
// import { FancyActionTypes } from '../actions/ticket-actions';

// start simple, make it work, _then_ think about how to make it generic

// FancyActionTypes
// consider changing to and "extension" of FancyActionTypes?
export enum GrouperUpdateTypes {
	Add,
	Change,
	SelfEditChange,
	Remove,
}

/**
 *
 */
export abstract class FancyGrouper<T, R extends ICombinedEdit<T>> {
	protected groupedSubject = new BehaviorSubject<R>(null);
	protected cachedList = new Map<string, T>();

	public grouped$ = this.groupedSubject.asObservable();

	constructor() {}

	protected abstract categorizeType(action: FancyAction<T>): GrouperUpdateTypes;

	public start(pseudoListEmitter: Observable<FancyAction<T>>) {
		pseudoListEmitter
			.pipe(
				// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7102
				map((action) => this.handleEmissions(action))
				//TODO - currently a delete event removing the final item will emit a broken "empty" grouped item
				// need to define the "final" way to handle that, currently filtering it out
				// uncommenting this breaks closing of edit
				// filter((groupedItem) => groupedItem?.srcList?.size > 0),
				// END PLACEHOLDER REPLACE AS PART OF TP-7102
			)
			.subscribe(this.groupedSubject);
	}

	private handleEmissions(action: FancyAction<T>) {
		const internalEventType = this.categorizeType(action);
		let newGroupedItem: R;

		switch (internalEventType) {
			case GrouperUpdateTypes.Add:
				newGroupedItem = this.addItem(action, this.groupedSubject.getValue(), this.cachedList);
				break;
			case GrouperUpdateTypes.Remove:
				newGroupedItem = this.removeItem(action, this.groupedSubject.getValue(), this.cachedList);
				break;
			case GrouperUpdateTypes.Change:
			case GrouperUpdateTypes.SelfEditChange:
			default:
				newGroupedItem = this.updateItem(action, this.groupedSubject.getValue(), this.cachedList);
				break;
		}

		newGroupedItem.lastEvent = action;
		newGroupedItem.lastGroupType = internalEventType;
		return newGroupedItem;
	}

	protected addItem(action: FancyAction<T>, lastGroupedItem: R, lastList: Map<string, T>) {
		lastList.set(action.key, action.payload);
		const newGroup = this.recalcGroup(action, lastGroupedItem, lastList);

		return newGroup;
	}
	protected updateItem(action: FancyAction<T>, lastGroupedItem: R, lastList: Map<string, T>) {
		//this update type can be made more optimized, not worth in first cut, probably
		lastList.set(action.key, action.payload);
		const newGroup = this.recalcGroup(action, lastGroupedItem, lastList);

		return newGroup;
	}
	protected removeItem(action: FancyAction<T>, lastGroupedItem: R, lastList: Map<string, T>) {
		lastList.delete(action.key);
		const newGroup = this.recalcGroup(action, lastGroupedItem, lastList);

		return newGroup;
	}

	// rebuild the group from scratch
	protected abstract recalcGroup(action: FancyAction<T>, lastGroupedItem: R, lastList: Map<string, T>): R;
}

export class FancySimpleTicketGrouper extends FancyGrouper<Ticket, GroupedTicket> {
	protected addActions = ticketAddActions;
	protected removeActions = ticketRemoveActions;

	protected categorizeType(action: FancyAction<Ticket>) {
		//TODO - finish (changed in edit one)
		if (isActionIncluded(action, this.addActions)) {
			return GrouperUpdateTypes.Add;
		} else if (isActionIncluded(action, this.removeActions)) {
			return GrouperUpdateTypes.Remove;
		} else {
			return GrouperUpdateTypes.Change;
		}
	}

	protected recalcGroup(
		action: FancyAction<Ticket>,
		lastGroupedItem: GroupedTicket,
		lastList: Map<string, Ticket>
	) {
		const gt = new GroupedTicket();
		gt.updateFromTickets(lastList);
		return gt; //TODO - finish (probably use the same instance?)
	}
}
